import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, Loader2, Wallet, Calculator, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, BarChart, Bar,
} from 'recharts';
import { useStatsBlockGuard } from '../hooks/useStatsBlockGuard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtCA = (n) => `${(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;

export default function StatistiquesCA() {
    useStatsBlockGuard('stats_ca');
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_URL}/api/statistiques/chiffre-affaires`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    const totals = data?.totals || {};
    const months = data?.months || [];
    const top = data?.top_clients || [];
    const evol = totals.evolution_pct;

    const KPI = ({ label, value, icon: Icon, color, hint }) => (
        <Card className="shadow-sm">
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase text-gray-500 tracking-wide">{label}</p>
                        <p className="text-2xl font-bold text-[#1B365D] mt-1">{value}</p>
                        {hint ? <p className="text-xs text-gray-400 mt-0.5">{hint}</p> : null}
                    </div>
                    <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
                        <Icon className="h-5 w-5" style={{ color }} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <Layout>
            <div className="space-y-6 animate-fadeIn" data-testid="ca-page">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/statistiques')} className="h-8 w-8 p-0">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-violet-500" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Chiffre d'affaires</h1>
                        <p className="text-gray-500 mt-0.5 text-sm">Tendance 12 mois & comparaison N/N-1</p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#1B365D]" /></div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPI label="CA 12 mois" value={fmtCA(totals.total_12m)} icon={Wallet} color="#8B5CF6" hint={`${totals.count_12m || 0} prestations`} />
                            <KPI label="Ticket moyen" value={fmtCA(totals.ticket_moyen)} icon={Calculator} color="#0EA5E9" />
                            <KPI label="Mois en cours" value={fmtCA(totals.current_month_ca)} icon={TrendingUp} color="#10B981" hint={evol != null ? `vs N-1: ${evol >= 0 ? '+' : ''}${evol}%` : 'pas d\'historique'} />
                            <KPI label="Prévision mois" value={fmtCA(totals.forecast_current_month)} icon={evol != null && evol >= 0 ? ArrowUpRight : ArrowDownRight} color="#C9A962" hint="à ce rythme" />
                        </div>

                        <Card className="shadow-sm">
                            <CardHeader className="pb-2"><CardTitle className="text-lg text-[#1B365D]">Évolution mensuelle (CA en €)</CardTitle></CardHeader>
                            <CardContent>
                                <div style={{ width: '100%', height: 320 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={months}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip formatter={(v) => fmtCA(v)} />
                                            <Legend />
                                            <Line type="monotone" dataKey="ca" name="CA (année courante)" stroke="#8B5CF6" strokeWidth={2.5} dot={{ r: 3 }} />
                                            <Line type="monotone" dataKey="ca_last_year" name="CA N-1" stroke="#94A3B8" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="shadow-sm">
                            <CardHeader className="pb-2"><CardTitle className="text-lg text-[#1B365D]">Volume de prestations par mois</CardTitle></CardHeader>
                            <CardContent>
                                <div style={{ width: '100%', height: 220 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={months}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip />
                                            <Bar dataKey="count" name="Prestations" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="shadow-sm">
                            <CardHeader className="pb-2"><CardTitle className="text-lg text-[#1B365D]">Top 5 plus grosses prestations</CardTitle></CardHeader>
                            <CardContent>
                                {top.length === 0 ? (
                                    <p className="text-center text-gray-400 py-6">Aucune donnée</p>
                                ) : (
                                    <div className="divide-y">
                                        {top.map((c, i) => (
                                            <div key={c.id || i} className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-4 px-4 cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>
                                                <div>
                                                    <p className="font-medium text-[#1B365D]">{c.nom}</p>
                                                    <p className="text-xs text-gray-400">{c.formule} · {c.date_demenagement}</p>
                                                </div>
                                                <p className="font-bold text-[#C9A962]">{fmtCA(c.total_ttc)}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </Layout>
    );
}
